import { MagnifyingGlass, MapPin, House, Buildings, Bed, Bathtub, Shield, SwimmingPool, Television, Ruler, Info, Car, FirstAid, GraduationCap, Database, SpinnerGap, ArrowClockwise, CheckCircle, Warning, UploadSimple, ChartLine, DownloadSimple, BookOpen } from "@phosphor-icons/react";
import { Switch } from "./components/ui/switch";
import { Label } from "./components/ui/label";

export function Sidebar({
  address, setAddress, setUseCoordinates, handlePredict, resolvedAddress,
  useCoordinates, latitude, longitude,
  metros, handleMetroSelect,
  bedrooms, setBedrooms, bathrooms, setBathrooms,
  hasSecurity, setHasSecurity, hasPool, setHasPool, hasDstv, setHasDstv,
  propertySize, setPropertySize,
  prediction, distances,
  dataHealth, scraping, handleScrapeData, importing, handleExcelImport,
  isAdmin, loading, generatingPdf,
  handleDownloadReport, handleNewValuation, handleDownloadManual,
}) {
  return (
    <aside className="sidebar-dark" data-testid="sidebar">
      <div className="logo-section">
        <h1 className="logo-text" data-testid="app-title">N K O S I'S</h1>
      </div>

      <div className="sidebar-content">
        {/* Address Input */}
        <div className="sidebar-section-dark">
          <label className="form-label-dark">
            <MagnifyingGlass size={14} weight="bold" className="inline mr-2 opacity-70" />
            Property Address
          </label>
          <input
            type="text"
            className="form-input-dark"
            placeholder="e.g., 123 Main Street, Sandton"
            value={address}
            onChange={(e) => { setAddress(e.target.value); setUseCoordinates(false); }}
            onKeyDown={(e) => e.key === "Enter" && handlePredict()}
            data-testid="address-input"
          />
          {resolvedAddress && (
            <p className="text-xs text-emerald-400 mt-2 truncate" title={resolvedAddress}>
              <CheckCircle size={12} className="inline mr-1" />
              {resolvedAddress.substring(0, 40)}...
            </p>
          )}
          <p className="text-xs text-slate-400 mt-2">Or click on the map to select location</p>
        </div>

        {/* Coordinates Display */}
        {useCoordinates && latitude && longitude && (
          <div className="sidebar-section-dark bg-slate-800/50 border-l-2 border-cyan-400">
            <label className="form-label-dark">
              <MapPin size={14} weight="bold" className="inline mr-2 opacity-70" />
              Selected Coordinates
            </label>
            <div className="grid grid-cols-2 gap-2 text-sm text-white">
              <div><span className="text-slate-400">Lat:</span> {latitude}</div>
              <div><span className="text-slate-400">Lng:</span> {longitude}</div>
            </div>
          </div>
        )}

        {/* Quick Metro Select */}
        <div className="sidebar-section-dark">
          <label className="form-label-dark">
            <Buildings size={14} weight="bold" className="inline mr-2 opacity-70" />
            Quick Select Metro
          </label>
          <select
            className="form-input-dark"
            value=""
            onChange={(e) => {
              const metro = metros.find(m => m.name === e.target.value);
              if (metro) handleMetroSelect(metro);
            }}
            data-testid="metro-select"
          >
            <option value="" disabled>Select a metro area...</option>
            {metros.map((metro) => (
              <option key={metro.name} value={metro.name}>{metro.name}</option>
            ))}
          </select>
        </div>

        {/* Property Features */}
        <div className="sidebar-section-dark">
          <label className="form-label-dark">
            <House size={14} weight="bold" className="inline mr-2 opacity-70" />
            Property Features
            <span className="text-slate-500 font-normal ml-2">(optional)</span>
          </label>
          <p className="text-xs text-slate-400 mb-3">Default: 1 bedroom, 1 bathroom</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Bed size={16} weight="duotone" className="text-slate-400" />
                <span className="text-xs text-slate-300">Bedrooms</span>
              </div>
              <div className="stepper-dark" data-testid="bedrooms-stepper">
                <button className="stepper-btn" onClick={() => setBedrooms(Math.max(1, (bedrooms ?? 1) - 1))} data-testid="bedrooms-minus">-</button>
                <input type="number" className="stepper-input" value={bedrooms ?? 1} min={1} max={10}
                  onChange={(e) => setBedrooms(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))} data-testid="bedrooms-input" />
                <button className="stepper-btn" onClick={() => setBedrooms(Math.min(10, (bedrooms ?? 1) + 1))} data-testid="bedrooms-plus">+</button>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Bathtub size={16} weight="duotone" className="text-slate-400" />
                <span className="text-xs text-slate-300">Bathrooms</span>
              </div>
              <div className="stepper-dark" data-testid="bathrooms-stepper">
                <button className="stepper-btn" onClick={() => setBathrooms(Math.max(1, (bathrooms ?? 1) - 1))} data-testid="bathrooms-minus">-</button>
                <input type="number" className="stepper-input" value={bathrooms ?? 1} min={1} max={10}
                  onChange={(e) => setBathrooms(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))} data-testid="bathrooms-input" />
                <button className="stepper-btn" onClick={() => setBathrooms(Math.min(10, (bathrooms ?? 1) + 1))} data-testid="bathrooms-plus">+</button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="switch-container-dark">
              <Label className="switch-label-dark"><Shield size={16} weight="duotone" className="text-slate-400" /> Security</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{hasSecurity ? 'Yes' : 'No'}</span>
                <Switch checked={hasSecurity} onCheckedChange={setHasSecurity} className="dark-switch" data-testid="security-switch" />
              </div>
            </div>
            <div className="switch-container-dark">
              <Label className="switch-label-dark"><SwimmingPool size={16} weight="duotone" className="text-slate-400" /> Swimming Pool</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{hasPool ? 'Yes' : 'No'}</span>
                <Switch checked={hasPool} onCheckedChange={setHasPool} className="dark-switch" data-testid="pool-switch" />
              </div>
            </div>
            <div className="switch-container-dark">
              <Label className="switch-label-dark"><Television size={16} weight="duotone" className="text-slate-400" /> DSTV</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{hasDstv ? 'Yes' : 'No'}</span>
                <Switch checked={hasDstv} onCheckedChange={setHasDstv} className="dark-switch" data-testid="dstv-switch" />
              </div>
            </div>
          </div>
        </div>

        {/* Property Size */}
        <div className="sidebar-section-dark">
          <div className="flex items-center gap-2 mb-2">
            <Ruler size={16} weight="duotone" className="text-slate-400" />
            <span className="text-xs text-slate-300">Property Size (m²)</span>
            <span className="text-xs text-slate-500 ml-auto">optional</span>
          </div>
          <input type="number" className="form-input-dark" placeholder="Leave blank to auto-estimate"
            value={propertySize ?? ''} min={20} max={1000}
            onChange={(e) => setPropertySize(e.target.value === '' ? null : Math.min(1000, Math.max(20, parseInt(e.target.value) || 0)))}
            data-testid="property-size-input" />
        </div>

        {/* Auto-calculated Distances */}
        {prediction?.distance_factors && (
          <div className="sidebar-section-dark bg-slate-800/50 border-l-2 border-cyan-400">
            <label className="form-label-dark"><Info size={14} weight="bold" className="inline mr-2 opacity-70" /> Auto-Calculated Distances</label>
            <div className="space-y-2 text-xs text-slate-300">
              <div className="flex justify-between"><span className="flex items-center gap-1"><Car size={14} className="text-slate-400" /> Highway:</span><span className="font-medium text-white">{prediction.distance_factors.distance_highway} km</span></div>
              <div className="flex justify-between"><span className="flex items-center gap-1"><FirstAid size={14} className="text-slate-400" /> Healthcare:</span><span className="font-medium text-white">{prediction.distance_factors.distance_healthcare} km</span></div>
              <div className="flex justify-between"><span className="flex items-center gap-1"><GraduationCap size={14} className="text-slate-400" /> Learning:</span><span className="font-medium text-white">{prediction.distance_factors.distance_learning} km</span></div>
            </div>
          </div>
        )}

        {/* Data Health */}
        <div className="sidebar-section-dark border-t border-slate-700 pt-4">
          <label className="form-label-dark"><Database size={14} weight="bold" className="inline mr-2 opacity-70" /> Data Health</label>
          {dataHealth && (
            <div className="bg-slate-800/50 p-3 mb-3 text-xs space-y-1 rounded">
              <div className="flex justify-between text-slate-300"><span>Properties:</span><span className="font-semibold text-white">{dataHealth.total_properties}</span></div>
              <div className="flex justify-between text-slate-300"><span>Metros:</span><span className="font-semibold text-white">{dataHealth.metros_covered}</span></div>
              <div className="flex justify-between text-slate-300"><span>Quality:</span><span className="font-semibold text-white">{dataHealth.data_quality_score}/10</span></div>
              <div className="flex justify-between items-center text-slate-300">
                <span>Status:</span>
                <span className={`font-semibold flex items-center gap-1 ${dataHealth.scraping_status === 'complete' ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {dataHealth.scraping_status === 'complete' ? <CheckCircle size={12} /> : <Warning size={12} />}
                  {dataHealth.scraping_status}
                </span>
              </div>
            </div>
          )}
          {isAdmin && (
            <>
              <button onClick={handleScrapeData} disabled={scraping} className="btn-outline-dark" data-testid="scrape-btn">
                {scraping ? <><SpinnerGap size={16} className="animate-spin" /> Scraping P24 + PP...</> : <><ArrowClockwise size={16} weight="bold" /> Scrape Live Data</>}
              </button>
              <label className="btn-outline-dark cursor-pointer mt-2" data-testid="import-excel-btn">
                {importing ? <><SpinnerGap size={16} className="animate-spin" /> Importing...</> : <><UploadSimple size={16} weight="bold" /> Import Excel Data</>}
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelImport} disabled={importing} data-testid="import-file-input" />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Pinned Action Buttons */}
      <div className="sidebar-actions-pinned" data-testid="sidebar-actions">
        <button onClick={handlePredict} disabled={loading} className="btn-primary-dark" data-testid="predict-btn">
          {loading ? <><SpinnerGap size={18} className="animate-spin" /> Calculating...</> : <><ChartLine size={18} weight="bold" /> Run Valuation</>}
        </button>
        {prediction && (
          <>
            <button onClick={() => handleDownloadReport("site")} disabled={generatingPdf} className="btn-secondary-dark" data-testid="site-report-btn">
              {generatingPdf ? <><SpinnerGap size={18} className="animate-spin" /> Generating...</> : <><DownloadSimple size={18} weight="bold" /> Site Valuation Report</>}
            </button>
            <button onClick={() => handleDownloadReport("property")} disabled={generatingPdf} className="btn-outline-dark" data-testid="property-report-btn">
              <DownloadSimple size={16} weight="bold" /> Property Valuation Report
            </button>
            <button onClick={handleNewValuation} className="btn-outline-dark" data-testid="new-valuation-btn">
              <ArrowClockwise size={16} weight="bold" /> New Valuation
            </button>
          </>
        )}
        {isAdmin && (
          <button onClick={handleDownloadManual} className="btn-outline-dark" data-testid="download-manual-btn">
            <BookOpen size={16} weight="bold" /> Download Technical Manual
          </button>
        )}
      </div>
    </aside>
  );
}
